/**
 * t310u: design tokens and small formatters shared by the phone web UI (apps/web/src/mobile) and the native app
 * (apps/mobile), so both draw the same colours, avatars and times. The web keeps its Tailwind classes; these are
 * the values behind them (Tailwind palette in sRGB hex, the brand tokens from apps/web/src/index.css).
 */
import { DateTime } from 'luxon';
import { formatClock, type ClockPref } from '../timeFormat';

/**
 * Brand tokens (index.css :root). Yellow is a fill with black content; never text.
 *
 * t374u: these are Trade Outlet's values; the native app now takes each company's from branding/native.ts
 * (brandPalette), where `yellow` means the accent fill and `black` the chrome. `onYellow` is the text and icons on
 * the accent fill, `chromeAccent` the accent on the chrome (with `onChromeAccent` on it), `chromeBar` and
 * `chromeActive` the tab bar and its selected label, `accentText` the accent as text on white (ink when it can't be).
 */
export const BRAND = {
  yellow: '#fff200', yellowHover: '#ebdf00', yellowPressed: '#d6cb00', yellowTint: '#fffbc2',
  black: '#0a0a0a', blackSoft: '#1f1f1f', blackRaised: '#2e2e2e',
  onYellow: '#000000', chromeAccent: '#fff200', onChromeAccent: '#0a0a0a', chromeBar: '#000000', chromeActive: '#f5c400',
  accentText: '#0a0a0a',
  surface: '#ffffff', surfaceFg: '#0f172a',
  success: '#047857', warning: '#c2410c', danger: '#be123c', info: '#0369a1',
  bubbleMine: '#fffbc2', bubbleOther: '#f1f5f9', bubbleMeta: '#475569',
  reactionMine: '#fffbc2', onReactionMine: '#1e293b',
} as const;

/** The Tailwind colours the phone UI uses. */
export const TW = {
  white: '#ffffff', black: '#000000',
  slate: { 50: '#f8fafc', 100: '#f1f5f9', 200: '#e2e8f0', 300: '#cbd5e1', 400: '#94a3b8', 500: '#64748b', 600: '#475569', 700: '#334155', 800: '#1e293b', 900: '#0f172a', 950: '#020617' },
  rose: { 50: '#fff1f2', 100: '#ffe4e6', 300: '#fda4af', 400: '#fb7185', 500: '#f43f5e', 600: '#e11d48', 700: '#be123c', 900: '#881337' },
  red: { 600: '#dc2626' },
  orange: { 50: '#fff7ed', 100: '#ffedd5', 600: '#ea580c', 700: '#c2410c', 800: '#9a3412' },
  amber: { 50: '#fffbeb', 100: '#fef3c7', 500: '#f59e0b', 600: '#d97706', 700: '#b45309', 800: '#92400e', 900: '#78350f' },
  yellow: { 100: '#fef9c3', 900: '#713f12' },
  emerald: { 50: '#ecfdf5', 100: '#d1fae5', 400: '#34d399', 500: '#10b981', 600: '#059669', 700: '#047857', 800: '#065f46', 900: '#064e3b' },
  blue: { 600: '#2563eb' },
  sky: { 100: '#e0f2fe', 700: '#0369a1', 800: '#075985', 900: '#0c4a6e' },
  violet: { 100: '#ede9fe', 700: '#6d28d9' },
  pink: { 100: '#fce7f3', 700: '#be185d' },
} as const;

/** The shape of TW / TW_DARK (and BRAND / BRAND_DARK), without the literal types, so either can be used. */
export type Palette = { readonly [K in keyof typeof TW]: (typeof TW)[K] extends string ? string : { readonly [S in keyof (typeof TW)[K]]: string } };
export type BrandPalette = { readonly [K in keyof typeof BRAND]: string };

/**
 * t335u item 8c: the app's dark mode, the same steps as the web's `dark:` classes. Slate turns over (a slate-50 page is
 * slate-950, slate-900 text is near white, slate-200 lines are slate-700); the hue tints (50 / 100) become dark tints and
 * the hue text shades (700-900) the light ones (rose-700 text reads as rose-400). The 400-600 fills stay. White and black
 * stay as they are: they're used on coloured fills and for the black chrome.
 */
export const TW_DARK: Palette = {
  white: '#ffffff', black: '#000000',
  slate: { 50: '#020617', 100: '#1e293b', 200: '#334155', 300: '#475569', 400: '#64748b', 500: '#94a3b8', 600: '#cbd5e1', 700: '#e2e8f0', 800: '#f1f5f9', 900: '#f8fafc', 950: '#ffffff' },
  rose: { 50: '#2a0a14', 100: '#4c0519', 300: '#9f1239', 400: '#fb7185', 500: '#f43f5e', 600: '#e11d48', 700: '#fb7185', 900: '#fecdd3' },
  red: { 600: '#dc2626' },
  orange: { 50: '#2a1205', 100: '#431407', 600: '#ea580c', 700: '#fb923c', 800: '#fdba74' },
  amber: { 50: '#2a1a04', 100: '#451a03', 500: '#f59e0b', 600: '#d97706', 700: '#fbbf24', 800: '#fcd34d', 900: '#fde68a' },
  yellow: { 100: '#422006', 900: '#fef08a' },
  emerald: { 50: '#04241c', 100: '#022c22', 400: '#34d399', 500: '#10b981', 600: '#059669', 700: '#34d399', 800: '#6ee7b7', 900: '#a7f3d0' },
  blue: { 600: '#3b82f6' },
  sky: { 100: '#082f49', 700: '#38bdf8', 800: '#7dd3fc', 900: '#bae6fd' },
  violet: { 100: '#2e1065', 700: '#a78bfa' },
  pink: { 100: '#500724', 700: '#f472b6' },
};

/** BRAND in dark mode (index.css `.dark`). Yellow and black stay; the tints, the page surface and the bubbles go dark. */
export const BRAND_DARK: BrandPalette = {
  ...BRAND,
  yellowTint: '#2f2c00', accentText: '#f1f5f9',
  surface: '#020617', surfaceFg: '#f1f5f9',
  success: '#34d399', warning: '#fb923c', danger: '#fb7185', info: '#38bdf8',
  bubbleMine: '#2f2c00', bubbleOther: '#1e293b', bubbleMeta: '#cbd5e1',
  reactionMine: '#34310a', onReactionMine: '#f8fafc',
};

/** Avatar / fallback chip colours. White initials sit on these (WCAG AA against white). No yellows. */
export const AVATAR_PALETTE = ['#4f46e5', '#0369a1', '#0f766e', '#15803d', '#c2410c', '#dc2626', '#be185d', '#7c3aed', '#0e7490', '#4d7c0f'];

/** Stable colour for an id (avatars, fallback chips). */
export function colorFor(id: string) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length]!;
}

/** "Phoebe Mensah" → "PM", "Daniel" → "D", nothing → "?". */
export function avatarInitials(name: string | null | undefined) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1]![0] : '')).toUpperCase();
}

const LONDON = 'Europe/London';
const at = (iso: string, zone: string) => DateTime.fromISO(iso, { zone: 'utc' }).setZone(zone);

/**
 * "just now", "1 min ago", "5 mins ago", "3 hours ago", "yesterday", then "14 Oct" / "14 Oct 2025" (en-GB). Never
 * "in …" (clock skew reads "just now").
 */
export function relativeAgo(iso: string, now: DateTime = DateTime.now().setZone(LONDON), zone = LONDON) {
  const d = at(iso, zone);
  if (!d.isValid) return '';
  const secs = Math.max(0, now.diff(d, 'seconds').seconds);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return mins === 1 ? '1 min ago' : `${mins} mins ago`;
  const hours = Math.floor(mins / 60);
  // Hours for today, and for the small hours (10 pm last night reads "2 hours ago" at 00:30, not "yesterday").
  if (hours < 24 && (d.hasSame(now, 'day') || hours < 6)) return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  if (d.hasSame(now.minus({ days: 1 }), 'day')) return 'yesterday';
  return d.toFormat(d.hasSame(now, 'year') ? 'd LLL' : 'd LLL yyyy');
}

/**
 * Chat message and thread times: "Just now", "5 min ago", "14:05" today, "Yesterday 14:05", "18 Oct, 06:05", then
 * "18 Oct 2025, 06:05". `inline` lower-cases the words for use mid-sentence.
 */
export function messageTimeText(iso: string, opts: { now?: DateTime; inline?: boolean; pref?: ClockPref; zone?: string } = {}) {
  const zone = opts.zone ?? LONDON;
  const d = at(iso, zone);
  if (!d.isValid) return '';
  const clock = (x: DateTime) => formatClock(x, opts.pref ?? '24h');
  const now = (opts.now ?? DateTime.now()).setZone(zone);
  const secs = Math.max(0, now.diff(d, 'seconds').seconds);
  if (secs < 60) return opts.inline ? 'just now' : 'Just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} min ago`;
  if (d.hasSame(now, 'day')) return clock(d);
  if (d.hasSame(now.minus({ days: 1 }), 'day')) return `${opts.inline ? 'yesterday' : 'Yesterday'} ${clock(d)}`;
  if (d.hasSame(now, 'year')) return `${d.toFormat('d LLL')}, ${clock(d)}`;
  return `${d.toFormat('d LLL yyyy')}, ${clock(d)}`;
}

/** Chat day separators: "Today", "Yesterday", "Monday 19 October" (with the year when it isn't this year). */
export function dayDividerText(iso: string, now: DateTime = DateTime.now().setZone(LONDON), zone = LONDON) {
  const d = at(iso, zone);
  if (d.hasSame(now, 'day')) return 'Today';
  if (d.hasSame(now.minus({ days: 1 }), 'day')) return 'Yesterday';
  return d.toFormat(d.hasSame(now, 'year') ? 'cccc d LLLL' : 'cccc d LLLL yyyy');
}

/** "1 person", "4 people": the muted reactions summary. */
export const peopleLabel = (n: number) => `${n} ${n === 1 ? 'person' : 'people'}`;
